import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMenuContext } from '../providers/MenuProvider';

interface ProtectedRouteProps {
  children: React.ReactNode;
  path?: string;
}

export function ProtectedRoute({ children, path }: ProtectedRouteProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { hasAccess, loading, accessiblePaths } = useMenuContext();

  // Use provided path or current location
  const routePath = path || location.pathname;

  // Check if user has access
  const canAccess = hasAccess(routePath);

  // Redirect if no access
  useEffect(() => {
    if (!loading && !canAccess) {
      // Find the first accessible path to redirect to
      const redirectPath = accessiblePaths.length > 0 ? accessiblePaths[0] : '/';
      navigate(redirectPath, { replace: true });
    }
  }, [loading, canAccess, accessiblePaths, navigate]);

  // Show loading state while checking permissions
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    );
  }

  // If no access, show nothing (will redirect)
  if (!canAccess) {
    return null;
  }

  return <>{children}</>;
}

// Higher-order component version
export function withRouteProtection<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  path?: string
) {
  return function ProtectedComponent(props: P) {
    return (
      <ProtectedRoute path={path}>
        <WrappedComponent {...props} />
      </ProtectedRoute>
    );
  };
}
